<script lang="ts">
  import { formatDailyAverageScore, formatDistance, getMode, CONTINENT_BY_SLUG } from '@atlasguess/games';
  import EmptyState from '../../components/ui/EmptyState.svelte';
  import Button from '../../components/ui/Button.svelte';
  import Spinner from '../../components/ui/Spinner.svelte';
  import RecentDailiesTable from '../../components/RecentDailiesTable.svelte';
  import { fetchAccountStats, formatQuizBest, type FullStats } from '../../lib/stats';
  import { displayUnit } from '../../lib/units';
  import { DAILY_ROUND_COUNT, FLAGSHIP_MODE_ID, MS_PER_DAY } from '../../lib/constants';
  import { AsyncData } from '../../lib/async-data.svelte';

  // Account → Stats tab. Loads the full payload from /api/account/stats
  // on mount and renders the flagship card up top, then a top-line
  // totals strip, per-format summaries, and per-mode table.
  //
  // EmptyState stays the fallback for accounts with zero rounds — no
  // sections render until the player has at least one row.

  const view = new AsyncData<FullStats>(fetchAccountStats);
  // Read-through alias so the rest of the script + template keeps the
  // `stats` name; the AsyncData wrapper holds the fetch lifecycle.
  const stats = $derived(view.data);
  const loading = $derived(view.loading);

  // Distance values come back as km from the server (server is unit-
  // agnostic). We translate to the user's display unit at render time so
  // toggling miles/km in /account immediately repaints these tiles too.
  const formatKm = (km: number | null): string =>
    km === null ? '—' : formatDistance(km, $displayUnit);

  // Mode IDs ("cities-world-daily") look ugly in a stats table. Look up
  // the registered mode and use its displayName ("Daily Cities"); fall
  // back to a slug-prettifier so an unregistered mode still renders
  // human-readable.
  function modeLabel(modeId: string): string {
    const mode = getMode(modeId);
    if (mode) return mode.displayName;
    return modeId
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  // Quiz card-specific label. Strips the "Countries"/"Quiz" framing
  // since the surrounding card title already says "Quiz" and the
  // entity is implied — just shows the continent name ("South
  // America"). Built off the mode_id regex so it works for any
  // future country-quiz region without touching the registry.
  function quizModeLabel(modeId: string): string {
    const m = modeId.match(/^countries-(.+)-quiz$/);
    if (m) {
      const slug = m[1];
      if (CONTINENT_BY_SLUG[slug]) return CONTINENT_BY_SLUG[slug];
      return slug
        .split('-')
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ');
    }
    return modeLabel(modeId);
  }

  function formatRelative(iso: string): string {
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / MS_PER_DAY);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    return new Date(iso).toLocaleDateString();
  }

  // Map a region-practice filter_slug to its display label. Empty
  // string is the unfiltered "All" view; known continent slugs use the
  // CONTINENT_BY_SLUG mapping; anything else falls back to a tidied
  // version of the slug itself (covers country-slug edge cases).
  function filterLabel(slug: string): string {
    if (slug === '') return 'All';
    if (CONTINENT_BY_SLUG[slug]) return CONTINENT_BY_SLUG[slug];
    return slug
      .split('-')
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ');
  }

  // Pluck the flagship row out of by_mode for the hero card. Other
  // modes are now displayed inside their respective format cards
  // (Practice Cities / Practice Countries / Quiz per_mode table) so
  // the dedicated "Other modes" table that used to live below has
  // been removed.
  const flagshipRow = $derived(
    stats?.by_mode.find((m) => m.mode_id === FLAGSHIP_MODE_ID) ?? null,
  );

</script>

{#if loading}
  <div class="stats-loading"><Spinner size="lg" /></div>
{:else if stats && stats.totals.rounds_played > 0}
  <div class="stats">
    <!-- Flagship: Daily Cities. Hero treatment so the headline stat the
         site cares most about doesn't disappear inside a uniform grid. -->
    <section class="stats__hero">
      <header class="stats__hero-header">
        <div>
          <p class="stats__hero-eyebrow">Flagship</p>
          <h3 class="stats__hero-title">{modeLabel(FLAGSHIP_MODE_ID)}</h3>
        </div>
        <Button variant="primary" size="sm" href="/">Play today</Button>
      </header>

      {#if flagshipRow && flagshipRow.best_total_score !== undefined}
        <div class="stats__hero-grid">
          <div class="stats__hero-stat">
            <div class="stats__hero-value">
              {formatDailyAverageScore(flagshipRow.best_total_score, DAILY_ROUND_COUNT)}
            </div>
            <div class="stats__hero-label">Best score</div>
          </div>
          <div class="stats__hero-stat">
            <div class="stats__hero-value">
              {formatDailyAverageScore(stats.by_format.daily.median_total_score, DAILY_ROUND_COUNT)}
            </div>
            <div class="stats__hero-label">Median score</div>
          </div>
          <div class="stats__hero-stat">
            <div class="stats__hero-value">
              {stats.streak.current_days}
              <span class="stats__hero-unit">days</span>
            </div>
            <div class="stats__hero-label">Current streak</div>
          </div>
        </div>
        <p class="stats__hero-meta">
          {flagshipRow.runs} run{flagshipRow.runs === 1 ? '' : 's'}
          &nbsp;·&nbsp; best streak {stats.streak.best_days} days
          &nbsp;·&nbsp; last played {formatRelative(flagshipRow.last_played)}
        </p>
      {:else}
        <p class="stats__hero-empty">
          You haven&rsquo;t played today&rsquo;s daily yet. Five rounds,
          fresh cities every day.
        </p>
      {/if}
    </section>

    <section class="stats__totals">
      <div class="stats__tile">
        <div class="stats__tile-value">{stats.totals.rounds_played.toLocaleString()}</div>
        <div class="stats__tile-label">Rounds played</div>
      </div>
      <div class="stats__tile">
        <div class="stats__tile-value">{stats.totals.modes_completed}</div>
        <div class="stats__tile-label">Modes played</div>
      </div>
    </section>

    {#if stats.recent_dailies.length > 0}
      <section class="stats__section">
        <h3 class="stats__section-title">Recent dailies</h3>
        <RecentDailiesTable rows={stats.recent_dailies} />
      </section>
    {/if}

    <section class="stats__section">
      <h3 class="stats__section-title">By format</h3>
      <div class="stats__format-grid">
        <div class="stats__format-card">
          <h4>Daily</h4>
          <dl>
            <div><dt>Runs</dt><dd>{stats.by_format.daily.runs}</dd></div>
            <div>
              <dt>Best score</dt>
              <dd>{formatDailyAverageScore(stats.by_format.daily.best_total_score, DAILY_ROUND_COUNT)}</dd>
            </div>
            <div>
              <dt>Median score</dt>
              <dd>{formatDailyAverageScore(stats.by_format.daily.median_total_score, DAILY_ROUND_COUNT)}</dd>
            </div>
          </dl>
        </div>
        <div class="stats__format-card">
          <h4>Practice — Cities</h4>
          <dl>
            <div><dt>Rounds</dt><dd>{stats.by_format.practice_cities.rounds}</dd></div>
            <div><dt>Median guess</dt><dd>{formatKm(stats.by_format.practice_cities.median_km)}</dd></div>
            <div><dt>Best guess</dt><dd>{formatKm(stats.by_format.practice_cities.best_km)}</dd></div>
          </dl>
        </div>
        <div class="stats__format-card">
          <h4>Practice — Countries</h4>
          <dl>
            <div><dt>Rounds</dt><dd>{stats.by_format.practice_countries.rounds}</dd></div>
            <div>
              <dt>Best streak</dt>
              <dd>
                {#if stats.by_format.practice_countries.streak_best !== null}
                  🔥 {stats.by_format.practice_countries.streak_best}
                {:else}
                  —
                {/if}
              </dd>
            </div>
          </dl>
          {#if stats.by_format.practice_countries.per_filter.length > 0}
            <ul class="stats__filter-list" aria-label="Best streak by region">
              {#each stats.by_format.practice_countries.per_filter as f (f.mode_id + ':' + f.filter_slug)}
                <li class="stats__filter-row">
                  <span class="stats__filter-name">{filterLabel(f.filter_slug)}</span>
                  <span class="stats__filter-streak">🔥 {f.streak_best}</span>
                </li>
              {/each}
            </ul>
          {/if}
        </div>
        <div class="stats__format-card">
          <h4>Quiz</h4>
          <dl>
            <div><dt>Runs</dt><dd>{stats.by_format.quiz.runs}</dd></div>
          </dl>
          {#if stats.by_format.quiz.per_mode.length > 0}
            <ul class="stats__quiz-list" aria-label="Best run by quiz mode">
              {#each stats.by_format.quiz.per_mode as q (q.mode_id)}
                <li class="stats__quiz-row">
                  <span class="stats__quiz-name">{quizModeLabel(q.mode_id)}</span>
                  <span class="stats__quiz-best">{formatQuizBest(q.best_run)}</span>
                </li>
              {/each}
            </ul>
          {/if}
        </div>
      </div>
    </section>
  </div>
{:else}
  <EmptyState
    title="No stats yet"
    body="Play a round and your scores, accuracy, and streaks will show up here."
  >
    {#snippet action()}
      <Button variant="primary" href="/">Play now</Button>
    {/snippet}
  </EmptyState>
{/if}
